import type { LensValueFormat } from '~/types/leLens';

import { formatDelta } from '../catalogue/commercial/derive';
import { formatLensValue } from '../lensChart';

import type { AreaContract } from './areaContract';

/** The fields every domain snapshot shares (generic, growth and loyalty shapes). */
export type SnapshotMetricLike = {
  id: string;
  label?: string;
  shortName?: string;
  name?: string;
  status?: string;
  value?: number | null;
  comparisonValue?: number | null;
  percentChange?: number | null;
  favourableDirection?: string;
  format?: LensValueFormat;
  /** A canary preview figure standing in for one the server does not serve yet (businessPulsePreview.ts). */
  preview?: boolean;
};

export type AreaMetricReading = {
  id: string;
  label: string;
  state: 'ready' | 'unavailable' | 'not-defined';
  value: string | null;
  /** "↑ 19.9%" for amounts, "↓ 0.1 pt" for metrics that are themselves percentages. Same glyphs as the Commercial strip. */
  changeText: string | null;
  tone: 'good' | 'bad' | 'neutral';
  format?: LensValueFormat;
};

export function headlineValue(value: number, format?: LensValueFormat): string {
  // A whole-number rate hides the movement the tile reports (19.7% vs 19.8% both read "20%").
  if (format?.kind === 'percent' && format.percentScale === 'unit') {
    const percent = value * 100;
    return `${percent.toFixed(Math.abs(percent) < 1 ? 2 : 1)}%`;
  }
  if (format?.kind === 'currency' && format.currency && Math.abs(value) >= 100_000) {
    return new Intl.NumberFormat(format.locale || 'en-AU', {
      currency: format.currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      notation: 'compact',
      style: 'currency',
    }).format(value);
  }
  if ((!format || format.kind === 'number') && Math.abs(value) >= 100_000) {
    return new Intl.NumberFormat('en-AU', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      notation: 'compact',
    }).format(value);
  }
  return formatLensValue(value, format);
}

const isPercentMetric = (format?: LensValueFormat) => format?.kind === 'percent';

function change(metric: SnapshotMetricLike): { text: string | null; delta: number | null } {
  const { value, comparisonValue, percentChange, format } = metric;
  // A rate moves in points; quoting "margin % down 0.7%" would be a percent of a percent.
  if (isPercentMetric(format) && value != null && comparisonValue != null) {
    const points = (value - comparisonValue) * 100;
    return { delta: points, text: `${points >= 0 ? '↑' : '↓'} ${Math.abs(points).toFixed(1)} pt` };
  }
  if (percentChange == null) {
    return { delta: null, text: null };
  }
  return { delta: percentChange, text: formatDelta(percentChange) };
}

function toneOf(delta: number | null, direction?: string): AreaMetricReading['tone'] {
  if (delta == null || delta === 0 || (direction !== 'up' && direction !== 'down')) {
    return 'neutral';
  }
  return (direction === 'up') === delta > 0 ? 'good' : 'bad';
}

/** The area's core metrics in contract order. Missing governed metrics read as unavailable, never as zero. */
export function readAreaMetrics(
  contract: Pick<AreaContract, 'metrics'>,
  metrics: readonly SnapshotMetricLike[] | undefined,
): AreaMetricReading[] {
  if (contract.metrics.kind === 'proposed') {
    return contract.metrics.labels.map((label) => ({
      changeText: null,
      id: label,
      label,
      state: 'not-defined',
      tone: 'neutral',
      value: null,
    }));
  }
  const governed = contract.metrics;
  const byId = new Map((metrics ?? []).map((metric) => [metric.id, metric]));
  return governed.metricIds.map((id) => {
    const metric = byId.get(id);
    const label = governed.labels?.[id] ?? metric?.shortName ?? metric?.label ?? metric?.name ?? id;
    if (!metric || (metric.status ?? 'ready') !== 'ready' || metric.value == null) {
      return {
        changeText: null,
        format: metric?.format,
        id,
        label,
        state: 'unavailable',
        tone: 'neutral',
        value: null,
      };
    }
    const { delta, text } = change(metric);
    return {
      changeText: text,
      format: metric.format,
      id,
      label,
      state: 'ready',
      tone: governed.neutral?.includes(id) ? 'neutral' : toneOf(delta, metric.favourableDirection),
      value: headlineValue(metric.value, metric.format),
    };
  });
}
